import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Table,
  Input,
  InputNumber,
  Button,
  notification,
  Avatar,
  Tooltip
} from 'antd';
import { Comment } from '@ant-design/compatible';
import moment from 'moment';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import {
  stateAtom,
  selectedSideAtom,
  refreshSidesAtom,
  selectedOptionAtom,
  businessAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { DraggableBodyRow, DragHandle } from '../../../utils/utils';

function ItemGtinsDrawer() {
  const currentState = useRecoilValue(stateAtom);
  const store = useRecoilValue(businessAtom);
  const setRefreshSides = useSetRecoilState(refreshSidesAtom);
  const setSelectedOption = useSetRecoilState(selectedOptionAtom);
  const [selectedSide, setSelectedSide] = useRecoilState(selectedSideAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const showMessage = function (message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  };

  const submitClick = function () {
    setLoading(true);
    const temp = JSON.parse(JSON.stringify(data));
    if (temp.items) {
      for (let i = 0; i < temp.items.length; i++) {
        temp.items[i].ordering = i;
        if (temp.items[i].id.startsWith('TEMP-')) {
          temp.items[i].id = null;
        }
      }
    }
    const payload = {
      side: temp,
      businessId: !currentState.globalReqSideAddons && store
        ? store.id : null
    };
    api
      .post(
        'deli/side/createorupdate',
        payload
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setRefreshSides(true);
          setSelectedSide(null);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  };

  const deleteClick = function () {
    setLoading(true);
    api
      .delete(`deli/side/${selectedSide.id}`)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setRefreshSides(true);
          setSelectedSide(null);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  };

  const removeOption = function (option) {
    const temp = JSON.parse(JSON.stringify(selectedSide));
    if (option.id) {
      temp.items = _.filter(temp.items, (o) => o.id !== option.id);
      if (!option.id.startsWith('TEMP-')) {
        api
          .post(
            `deli/delete/sideoption/${option.id}`,
            { }
          )
          .then((response) => {
            setLoading(false);
            if (response.data.success) {
              setSelectedSide(temp);
            } else {
              showMessage(response.data.error);
            }
          })
          .catch((error) => {
            setLoading(false);
            showMessage(error);
          });
      }
    } else {
      temp.items = _.filter(temp.items, (o) => o !== option);
      setSelectedSide(temp);
    }
  };

  const onDragEnd = ({ active, over }) => {
    const temp = JSON.parse(JSON.stringify(data));
    if (active.id !== over?.id) {
      setData((prevState) => {
        const activeIndex = prevState.items.findIndex((record) => record.id === active?.id);
        const overIndex = prevState.items.findIndex((record) => record.id === over?.id);
        const newData = arrayMove(prevState.items, activeIndex, overIndex);
        temp.items = JSON.parse(JSON.stringify(newData));
        return temp;
      });
    }
  };

  useEffect(() => {
    if (selectedSide) {
      const temp = JSON.parse(JSON.stringify(selectedSide));
      if (!temp.id) {
        temp.minRequired = 0;
        temp.maxRequired = 1;
      }
      setData(temp);
    } else {
      setData(selectedSide);
    }
  }, [selectedSide]);

  return (
    <Drawer
      title={`${selectedSide && selectedSide.id ? t('update') : t('create')} Side`}
      placement="right"
      closable
      onClose={() => setSelectedSide(null)}
      open={selectedSide}
      width={500}
    >
      <>
        {data && (
          <>
            {selectedSide.id && (
              <>
                <Button
                  type="primary"
                  danger
                  loading={loading}
                  style={{
                    margin: '10px 0',
                    float: 'right'
                  }}
                  onClick={() => deleteClick()}
                >
                  {t('delete')}
                </Button>
                <br />
              </>
            )}
            <div style={{ margin: '5px 0' }}>
              <strong>{t('internal_name')}:</strong>
              <Input
                disabled={loading}
                value={data.internalName}
                placeholder={t('internal_name')}
                onChange={(obj) => {
                  const temp = JSON.parse(JSON.stringify(data));
                  temp.internalName = obj.target.value;
                  setData(temp);
                }}
              />
            </div>
            <div style={{ margin: '5px 0' }}>
              <strong>{t('name')}:</strong>
              <Input
                disabled={loading}
                value={data.name}
                placeholder={t('name')}
                onChange={(obj) => {
                  const temp = JSON.parse(JSON.stringify(data));
                  temp.name = obj.target.value;
                  setData(temp);
                }}
              />
            </div>
            <div style={{ margin: '5px 0' }}>
              <strong>{t('minimum')}: </strong>
              <InputNumber
                disabled={loading}
                defaultValue={0}
                value={data.minRequired}
                minimum={0}
                onChange={(obj) => {
                  const temp = JSON.parse(JSON.stringify(data));
                  temp.minRequired = obj;
                  setData(temp);
                }}
              />
            </div>
            <div style={{ margin: '5px 0' }}>
              <strong>{t('maximum')}: </strong>
              <InputNumber
                disabled={loading}
                defaultValue={1}
                value={data.maxRequired}
                minimum={0}
                onChange={(obj) => {
                  const temp = JSON.parse(JSON.stringify(data));
                  temp.maxRequired = obj;
                  setData(temp);
                }}
              />
            </div>
            <strong>{t('options')}:</strong>
            <Button
              style={{ float: 'right' }}
              size="small"
              type="primary"
              onClick={() => {
                setSelectedSide(data);
                setSelectedOption({});
              }}
            >
              {t('add_new_option')}
            </Button>
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
              <SortableContext items={_.map(data?.items, (i) => i.id)} strategy={verticalListSortingStrategy}>
                <Table
                  size="small"
                  bordered
                  pagination={false}
                  dataSource={selectedSide.items}
                  rowKey="id"
                  components={{
                    body: {
                      row: DraggableBodyRow,
                    },
                  }}
                  columns={[
                    {
                      title: t('sort'),
                      dataIndex: 'sort',
                      width: 30,
                      key: 'sort',
                      className: 'drag-visible',
                      render: () => <DragHandle />,
                    },
                    {
                      title: t('image'),
                      align: 'center',
                      className: 'text-xs',
                      render: (row) => (
                        <>
                          {row.imageUrl && row.imageUrl.length > 0 && (
                            <img
                              style={{ margin: 'auto' }}
                              width={50}
                              src={row.imageUrl}
                              alt={row.name}
                            />
                          )}
                        </>
                      ),
                    },
                    {
                      title: t('name'),
                      dataIndex: 'name',
                      key: 'name',
                      align: 'center',
                      className: 'text-xs',
                      render: (text) => (
                        <span>{text}</span>
                      ),
                    },
                    {
                      title: t('descriptiom'),
                      dataIndex: 'description',
                      key: 'description',
                      align: 'center',
                      className: 'text-xs',
                      render: (text) => (
                        <span>{text}</span>
                      ),
                    },
                    {
                      title: '',
                      align: 'center',
                      className: 'text-xs',
                      render: (row) => (
                        <>
                          <Button
                            size="small"
                            type="primary"
                            style={{ marginRight: 3 }}
                            onClick={() => {
                              setSelectedSide(data);
                              setSelectedOption(row);
                            }}
                          >
                            {t('edit')}
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            danger
                            style={{ marginLeft: 3 }}
                            onClick={() => {
                              removeOption(row);
                            }}
                          >
                            {t('remove')}
                          </Button>
                        </>
                      ),
                    }
                  ]}
                />
              </SortableContext>
            </DndContext>
            <Button
              type="primary"
              loading={loading}
              style={{
                margin: '10px 0',
                float: 'right'
              }}
              onClick={() => submitClick()}
            >
              {selectedSide && selectedSide.id ? t('update') : t('create')}
            </Button>
          </>
        )}
      </>
    </Drawer>
  );
}
export default ItemGtinsDrawer;
